import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { toast } from "sonner";
import {
  Eye,
  EyeOff,
  Zap,
  ArrowRight,
  Loader2,
  Building2,
  Users,
  ShieldCheck,
  ArrowLeft,
  Mail,
  RefreshCw,
  CheckCircle,
  KeyRound,
  UserPlus,
} from "lucide-react";
import { PhoneInput } from "react-international-phone";
import { isValidPhoneNumber } from "libphonenumber-js";
import "react-international-phone/style.css";
import api from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((d) => d.msg || JSON.stringify(d)).join(", ");
  return JSON.stringify(detail);
}

// Defined OUTSIDE to prevent remount on every keystroke
function PasswordField({
  showPassword,
  onToggle,
  value,
  onChange,
  hint,
  disabled,
}) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
        Password
      </Label>
      {hint && (
        <p className="text-[11px] text-muted-foreground -mt-1">{hint}</p>
      )}
      <div className="relative">
        <Input
          type={showPassword ? "text" : "password"}
          placeholder="••••••••"
          value={value}
          onChange={onChange}
          required
          disabled={disabled}
          className="h-11 bg-secondary border-0 focus:ring-2 focus:ring-primary/30 pr-10"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}

const MEMBER_PWD_RULES = [
  { id: "len", label: "At least 6 characters", test: (p) => p.length >= 6 },
];
const STAFF_PWD_RULES = [
  { id: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
  {
    id: "upper",
    label: "One uppercase letter (A-Z)",
    test: (p) => /[A-Z]/.test(p),
  },
  {
    id: "lower",
    label: "One lowercase letter (a-z)",
    test: (p) => /[a-z]/.test(p),
  },
  { id: "num", label: "One number (0-9)", test: (p) => /[0-9]/.test(p) },
];

const FP_EMAIL = "fp_email";
const FP_OTP = "fp_otp";
const FP_NEWPWD = "fp_newpwd";
const FP_DONE = "fp_done";

function PwdStrengthBar({ password, rules }) {
  if (!password) return null;
  const passed = rules.filter((r) => r.test(password)).length;
  const pct = (passed / rules.length) * 100;
  const color =
    pct <= 25
      ? "bg-red-500"
      : pct <= 50
        ? "bg-orange-500"
        : pct <= 75
          ? "bg-yellow-500"
          : "bg-green-500";
  const label =
    pct <= 25 ? "Weak" : pct <= 50 ? "Fair" : pct <= 75 ? "Good" : "Strong";
  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${color}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span
          className={`text-xs font-medium ${
            pct <= 25
              ? "text-red-500"
              : pct <= 50
                ? "text-orange-500"
                : pct <= 75
                  ? "text-yellow-500"
                  : "text-green-500"
          }`}
        >
          {label}
        </span>
      </div>
      <ul className="space-y-1">
        {rules.map((rule) => {
          const ok = rule.test(password);
          return (
            <li
              key={rule.id}
              className={`flex items-center gap-1.5 text-xs ${ok ? "text-green-500" : "text-muted-foreground"}`}
            >
              <span className="font-bold">{ok ? "✓" : "✗"}</span> {rule.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ForgotPassword({ tab, orgSlug, onBack }) {
  const [fpStep, setFpStep] = useState(FP_EMAIL);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showCPwd, setShowCPwd] = useState(false);
  const [pwdFocused, setPwdFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const isStaff = tab === "staff";
  const rules = isStaff ? STAFF_PWD_RULES : MEMBER_PWD_RULES;
  const isPasswordValid = rules.every((r) => r.test(newPwd));

  const startTimer = () => {
    setResendTimer(60);
    const id = setInterval(() => {
      setResendTimer((t) => {
        if (t <= 1) {
          clearInterval(id);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isStaff) {
        await api.post("/auth/forgot-password", { email });
        toast.success(
          "If this email is registered, a reset code has been sent.",
        );
      } else {
        await api.post("/member-portal/forgot-password", { orgSlug, phone });
        toast.success(
          "If this phone number is registered, a reset code has been sent.",
        );
      }
      setFpStep(FP_OTP);
      startTimer();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      if (isStaff) {
        await api.post("/auth/forgot-password", { email });
      } else {
        await api.post("/member-portal/forgot-password", { orgSlug, phone });
      }
      toast.success("New reset code sent!");
      startTimer();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      if (isStaff) {
        await api.post("/auth/verify-reset-otp", { email, otp });
      } else {
        await api.post("/member-portal/verify-reset-otp", {
          orgSlug,
          phone,
          otp,
        });
      }
      toast.success("Code verified! Set your new password.");
      setFpStep(FP_NEWPWD);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!isPasswordValid) {
      toast.error("Please meet all password requirements.");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      if (isStaff) {
        await api.post("/auth/reset-password", {
          email,
          otp,
          new_password: newPwd,
        });
      } else {
        await api.post("/member-portal/reset-password", {
          orgSlug,
          phone,
          otp,
          new_password: newPwd,
        });
      }
      toast.success("Password reset successfully! Please sign in.");
      setFpStep(FP_DONE);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-card border-border shadow-xl">
      <CardHeader className="space-y-1 pb-6">
        <div className="flex items-center gap-2">
          {fpStep !== FP_DONE && (
            <button
              onClick={
                fpStep === FP_EMAIL
                  ? onBack
                  : () => {
                      if (fpStep === FP_OTP) setFpStep(FP_EMAIL);
                      if (fpStep === FP_NEWPWD) setFpStep(FP_OTP);
                    }
              }
              className="text-muted-foreground hover:text-foreground transition-colors mr-1"
              aria-label="Go back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <CardTitle className="text-2xl font-heading uppercase tracking-wide">
            {fpStep === FP_EMAIL && "Forgot Password"}
            {fpStep === FP_OTP && "Check Your Email"}
            {fpStep === FP_NEWPWD && "New Password"}
            {fpStep === FP_DONE && "All Done!"}
          </CardTitle>
        </div>
        <CardDescription>
          {fpStep === FP_EMAIL &&
            (isStaff
              ? "Enter your staff email and we'll send a reset code."
              : "Enter your registered phone number and we'll send a reset code to your email.")}
          {fpStep === FP_OTP && (
            <span className="flex items-center gap-2">
              <Mail className="h-4 w-4 shrink-0" />
              We sent a 6-digit code to your registered email address.
            </span>
          )}
          {fpStep === FP_NEWPWD &&
            "Choose a strong new password for your account."}
          {fpStep === FP_DONE && "Your password has been updated successfully."}
        </CardDescription>
      </CardHeader>

      <CardContent>
        {fpStep === FP_EMAIL && (
          <form onSubmit={handleRequestOTP} className="space-y-5">
            {isStaff ? (
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  Email Address
                </Label>
                <Input
                  type="email"
                  placeholder="staff@gym.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  disabled={loading}
                  className="h-11 bg-secondary border-0 focus:ring-2 focus:ring-primary/30"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  Phone Number
                </Label>
                <PhoneInput
                  defaultCountry="in"
                  value={phone}
                  onChange={setPhone}
                  disabled={loading}
                  inputClassName="flex h-11 w-full rounded-lg border border-input bg-secondary px-3 text-sm
                    focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary/40
                    bg-transparent outline-none flex-1 h-full"
                  className="w-full"
                />
                <p className="text-[11px] text-amber-500 font-medium leading-relaxed mt-2">
                  Please note: Password reset requires a registered email
                  address on your member profile. If you have not registered an
                  email with your gym, you cannot reset your password here —
                  please contact your gym administrator to reset it.
                </p>
              </div>
            )}
            <Button
              type="submit"
              className="w-full h-11 font-heading uppercase tracking-wider"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending code…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Send Reset Code <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          </form>
        )}

        {fpStep === FP_OTP && (
          <form onSubmit={handleVerifyOTP} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Verification Code
              </Label>
              <Input
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="123456"
                inputMode="numeric"
                maxLength={6}
                autoFocus
                disabled={loading}
                className="h-14 bg-secondary border-0 focus:ring-2 focus:ring-primary/30 text-center text-2xl font-mono tracking-widest"
              />
              <p className="text-xs text-muted-foreground">
                Code expires in 10 minutes
              </p>
            </div>
            <Button
              type="submit"
              className="w-full h-11 font-heading uppercase tracking-wider"
              disabled={loading || otp.length !== 6}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" /> Verify Code
                </span>
              )}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendTimer > 0 || loading}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend code"}
              </button>
            </div>
          </form>
        )}

        {fpStep === FP_NEWPWD && (
          <form onSubmit={handleReset} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                New Password
              </Label>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  placeholder={
                    isStaff ? "Min 8 characters" : "Min 6 characters"
                  }
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  onFocus={() => setPwdFocused(true)}
                  onBlur={() => setPwdFocused(false)}
                  required
                  disabled={loading}
                  className="h-11 bg-secondary border-0 focus:ring-2 focus:ring-primary/30 pr-11"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPwd ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {(pwdFocused || newPwd) && (
                <PwdStrengthBar password={newPwd} rules={rules} />
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  type={showCPwd ? "text" : "password"}
                  placeholder="Re-enter password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  required
                  disabled={loading}
                  className={`h-11 bg-secondary border-0 focus:ring-2 pr-11 ${
                    confirmPwd && newPwd !== confirmPwd
                      ? "ring-2 ring-red-500/50 focus:ring-red-500/50"
                      : confirmPwd && newPwd === confirmPwd
                        ? "ring-2 ring-green-500/50 focus:ring-green-500/50"
                        : "focus:ring-primary/30"
                  }`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowCPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showCPwd ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {confirmPwd && newPwd !== confirmPwd && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
              {confirmPwd && newPwd === confirmPwd && (
                <p className="text-xs text-green-500">Passwords match ✓</p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full h-11 font-heading uppercase tracking-wider"
              disabled={loading || !isPasswordValid || newPwd !== confirmPwd}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Resetting…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4" /> Reset Password
                </span>
              )}
            </Button>
          </form>
        )}

        {fpStep === FP_DONE && (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="h-9 w-9 text-green-500" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Your password has been reset. You can now sign in with your new
              password.
            </p>
            <Button
              onClick={onBack}
              className="w-full h-11 font-heading uppercase tracking-wider mt-2"
            >
              <span className="flex items-center gap-2">
                Back to Sign In <ArrowRight className="h-4 w-4" />
              </span>
            </Button>
          </div>
        )}

        {fpStep !== FP_DONE && (
          <div className="mt-6 pt-5 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              Remember your password?{" "}
              <button
                onClick={onBack}
                className="text-primary font-medium hover:underline"
              >
                Sign In
              </button>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function StaffLogin() {
  const { setTheme, setColorPreset } = useTheme();
  const { orgSlug } = useParams();
  const navigate = useNavigate();
  const { fetchUser } = useAuth();

  const [org, setOrg] = useState(null);

  useEffect(() => {
    if (org?.theme) {
      setTheme(org.theme);
    }
    if (org?.colorPreset) {
      setColorPreset(org.colorPreset);
    }
  }, [org?.theme, org?.colorPreset, setTheme, setColorPreset]);

  const [orgLoading, setOrgLoading] = useState(true);
  const [orgNotFound, setOrgNotFound] = useState(false);
  const [tab, setTab] = useState("staff");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const [staffForm, setStaffForm] = useState({ email: "", password: "" });
  const [memberPhone, setMemberPhone] = useState("");
  const [memberPassword, setMemberPassword] = useState("");
  const [enabledBranches, setEnabledBranches] = useState([]);
  const [showBranchSelect, setShowBranchSelect] = useState(false);

  const handleRegisterClick = () => {
    if (enabledBranches.length === 1) {
      navigate(`/public/signup/${org.id}/${enabledBranches[0].id}`);
    } else if (enabledBranches.length > 1) {
      setShowBranchSelect(true);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/auth/org/${orgSlug}`);
        setOrg(res.data.organization);
        setEnabledBranches(res.data.branches || []);
      } catch {
        setOrgNotFound(true);
      } finally {
        setOrgLoading(false);
      }
    })();
  }, [orgSlug]);

  useEffect(() => {
    if (org && org.forceMembersLoginPage) {
      setTab("member");
    }
  }, [org]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const memberToken = localStorage.getItem("member_token");
    const memberData = localStorage.getItem("member_data");
    if (token) {
      navigate("/", { replace: true });
    } else if (memberToken && memberData) {
      navigate("/member/dashboard", { replace: true });
    }
  }, [navigate, orgSlug]);

  const switchTab = (t) => {
    setTab(t);
    setShowPassword(false);
    setShowForgot(false);
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    if (!staffForm.email || !staffForm.password) {
      toast.error("Email and password required");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/staff-login", {
        orgSlug,
        email: staffForm.email,
        password: staffForm.password,
      });
      const { token, user } = res.data;
      localStorage.setItem("token", token);
      localStorage.setItem("org_slug", orgSlug);
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      await fetchUser();
      toast.success(`Welcome, ${user.name}!`);
      navigate("/");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleMemberSubmit = async (e) => {
    e.preventDefault();
    if (!memberPhone) {
      toast.error("Phone number required");
      return;
    }
    if (!isValidPhoneNumber(memberPhone)) {
      toast.error("Enter a valid phone number");
      return;
    }
    if (!memberPassword) {
      toast.error("Password required");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/member-login", {
        orgSlug,
        phone: memberPhone,
        password: memberPassword,
      });
      const { token, member } = res.data;
      localStorage.setItem("member_token", token);
      member.orgSlug = orgSlug;
      localStorage.setItem("member_data", JSON.stringify(member));
      localStorage.setItem("org_slug", orgSlug);
      toast.success(`Welcome, ${member.name}!`);
      navigate("/member/dashboard");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  if (orgLoading)
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );

  if (orgNotFound)
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 px-4">
        <Building2 className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-2xl font-bold font-heading">Gym not found</h1>
        <p className="text-muted-foreground text-center">
          The link you followed doesn't match any registered gym.
        </p>
      </div>
    );

  const logoSrc = org?.logo ? `${BACKEND_URL}${org.logo}` : null;

  return (
    <div className="min-h-screen bg-background flex pb-[calc(4rem+env(safe-area-inset-bottom,0px))] lg:pb-0">
      {/* Left decorative panel (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 bg-card border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10" />
        <div className="absolute top-1/4 -left-20 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <div className="flex items-center gap-3">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={org.name}
                className="h-12 w-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Zap className="h-6 w-6 text-primary" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-black font-heading text-primary tracking-wider">
                {org?.name}
              </h1>
              <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest">
                Portal
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <h2 className="text-4xl font-black font-heading uppercase leading-tight">
              {tab === "staff" ? (
                <>
                  {"Staff"}
                  <br />
                  <span className="text-primary">Sign In</span>
                </>
              ) : (
                <>
                  {"Member"}
                  <br />
                  <span className="text-primary">Sign In</span>
                </>
              )}
            </h2>
            <p className="text-muted-foreground text-lg max-w-md">
              {tab === "staff"
                ? "Use your credentials provided by your gym manager to access the staff dashboard."
                : "Login with your registered phone number to view your membership and attendance."}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            © 2026 Optimus Gym CRM. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile gym header */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={org.name}
                className="h-10 w-10 rounded-lg object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Zap className="h-5 w-5 text-primary" />
              </div>
            )}
            <h1 className="text-xl font-black font-heading text-primary tracking-wider">
              {org?.name}
            </h1>
          </div>

          {/* Desktop tab pill */}
          {!org?.forceMembersLoginPage && (
            <div className="hidden lg:flex rounded-lg bg-secondary p-1 mb-6 gap-1">
              {[
                ["staff", ShieldCheck, "Staff"],
                ["member", Users, "Member"],
              ].map(([key, Icon, label]) => (
                <button
                  key={key}
                  onClick={() => switchTab(key)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-all ${
                    tab === key
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" /> {label}
                </button>
              ))}
            </div>
          )}

          {showForgot ? (
            <ForgotPassword
              tab={tab}
              orgSlug={orgSlug}
              onBack={() => setShowForgot(false)}
            />
          ) : (
            <>
              {/* Staff card */}
              {tab === "staff" && (
                <Card className="bg-card border-border shadow-xl">
                  <CardHeader className="space-y-1 pb-6">
                    <CardTitle className="text-2xl font-heading uppercase tracking-wide">
                      Staff Sign In
                    </CardTitle>
                    <CardDescription>
                      {org?.name} — enter your staff credentials
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleStaffSubmit} className="space-y-5">
                      <div className="space-y-2">
                        <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                          Email
                        </Label>
                        <Input
                          type="email"
                          placeholder="staff@gym.com"
                          value={staffForm.email}
                          onChange={(e) =>
                            setStaffForm({
                              ...staffForm,
                              email: e.target.value,
                            })
                          }
                          required
                          disabled={loading}
                          className="h-11 bg-secondary border-0 focus:ring-2 focus:ring-primary/30"
                        />
                      </div>
                      <PasswordField
                        showPassword={showPassword}
                        onToggle={() => setShowPassword((s) => !s)}
                        value={staffForm.password}
                        onChange={(e) =>
                          setStaffForm({
                            ...staffForm,
                            password: e.target.value,
                          })
                        }
                        disabled={loading}
                      />
                      <div className="flex justify-end !mt-1">
                        <button
                          type="button"
                          onClick={() => setShowForgot(true)}
                          disabled={loading}
                          className="text-xs text-primary hover:underline"
                        >
                          Forgot?
                        </button>
                      </div>
                      <Button
                        type="submit"
                        className="w-full h-11 font-heading uppercase tracking-wider"
                        disabled={loading}
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Signing In...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            Sign In <ArrowRight className="h-4 w-4" />
                          </span>
                        )}
                      </Button>
                    </form>
                    <div className="mt-6 pt-6 border-t border-border text-center space-y-3">
                      {org?.forceMembersLoginPage && (
                        <p className="text-sm text-muted-foreground">
                          Are you a member?{" "}
                          <button
                            type="button"
                            onClick={() => switchTab("member")}
                            className="text-primary font-medium hover:underline ml-1"
                            data-testid="switch-to-member"
                          >
                            Member Sign In
                          </button>
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Are you the gym owner?{" "}
                        <a
                          href="/login"
                          className="text-primary font-medium hover:underline"
                        >
                          Owner Login
                        </a>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Member card */}
              {tab === "member" && (
                <Card className="bg-card border-border shadow-xl">
                  <CardHeader className="space-y-1 pb-6">
                    <CardTitle className="text-2xl font-heading uppercase tracking-wide">
                      Member Sign In
                    </CardTitle>
                    <CardDescription>
                      {org?.name} — enter your phone number
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleMemberSubmit} className="space-y-5">
                      <div className="space-y-2">
                        <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                          Phone Number
                        </Label>
                        <PhoneInput
                          defaultCountry="in"
                          value={memberPhone}
                          onChange={setMemberPhone}
                          disabled={loading}
                          inputClassName="flex h-11 w-full rounded-lg border border-input bg-secondary px-3 text-sm
                            focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary/40
                            bg-transparent outline-none flex-1 h-full"
                          className="w-full"
                        />
                      </div>
                      <PasswordField
                        showPassword={showPassword}
                        onToggle={() => setShowPassword((s) => !s)}
                        value={memberPassword}
                        onChange={(e) => setMemberPassword(e.target.value)}
                        hint=""
                        disabled={loading}
                      />
                      <div className="flex justify-end !mt-1">
                        <button
                          type="button"
                          onClick={() => setShowForgot(true)}
                          disabled={loading}
                          className="text-xs text-primary hover:underline"
                        >
                          Forgot?
                        </button>
                      </div>
                      <Button
                        type="submit"
                        className="w-full h-11 font-heading uppercase tracking-wider"
                        disabled={loading}
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Signing In...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            Sign In <ArrowRight className="h-4 w-4" />
                          </span>
                        )}
                      </Button>
                    </form>
                    <div className="mt-6 pt-6 border-t border-border text-center space-y-3">
                      {enabledBranches.length > 0 && (
                        <div className="space-y-2 pt-1">
                          <p className="text-sm text-muted-foreground">
                            Don't have an account?
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleRegisterClick}
                            className="w-full h-11 font-heading uppercase tracking-wider border-primary text-primary hover:bg-primary hover:text-primary-foreground group"
                            data-testid="register-link"
                          >
                            <span className="flex items-center justify-center gap-2">
                              Register
                              <UserPlus className="h-4 w-4  animate-pulse" />
                            </span>
                          </Button>
                        </div>
                      )}
                      {org?.forceMembersLoginPage && (
                        <p className="text-xs text-muted-foreground">
                          Are you staff?{" "}
                          <button
                            type="button"
                            onClick={() => switchTab("staff")}
                            className="text-primary font-medium hover:underline ml-1"
                            data-testid="switch-to-staff"
                          >
                            Staff Login
                          </button>
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* iOS-style bottom tab bar (mobile only) */}
      {!org?.forceMembersLoginPage && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-t border-border">
          <div className="flex items-stretch">
            {[
              ["staff", ShieldCheck, "Staff"],
              ["member", Users, "Member"],
            ].map(([key, Icon, label]) => (
              <button
                key={key}
                onClick={() => switchTab(key)}
                className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 transition-colors ${
                  tab === key ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <div
                  className={`relative flex items-center justify-center w-8 h-8 rounded-full transition-all ${
                    tab === key ? "bg-primary/10" : ""
                  }`}
                >
                  <Icon
                    className={`h-5 w-5 transition-transform ${tab === key ? "scale-110" : ""}`}
                  />
                  {tab === key && (
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                  )}
                </div>
                <span
                  className={`text-[10px] font-medium tracking-wide ${
                    tab === key ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  {label}
                </span>
              </button>
            ))}
          </div>
          <div style={{ height: "env(safe-area-inset-bottom, 0px)" }} />
        </div>
      )}

      {/* Branch Selection Dialog */}
      <Dialog open={showBranchSelect} onOpenChange={setShowBranchSelect}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide">
              Select Branch
            </DialogTitle>
            <DialogDescription>
              Please choose a branch to register at
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {enabledBranches.map((b) => (
              <Button
                key={b.id}
                variant="outline"
                className="w-full justify-start text-left h-12 hover:bg-secondary"
                onClick={() => {
                  setShowBranchSelect(false);
                  navigate(`/public/signup/${org.id}/${b.id}`);
                }}
                data-testid={`select-branch-${b.id}`}
              >
                <Building2 className="mr-2 h-4 w-4 text-primary shrink-0" />
                <span>{b.name}</span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
